import { api } from '@/api/client';
import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  toast,
} from '@kgc/ui';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { PublishedWorkflow, WorkflowParameter } from '../types';

interface ExecuteWorkflowDialogProps {
  workflow: PublishedWorkflow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  initialParameters?: Record<string, unknown> | undefined;
}

export function ExecuteWorkflowDialog({
  workflow,
  open,
  onOpenChange,
  onSuccess,
  initialParameters,
}: ExecuteWorkflowDialogProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open && initialParameters) {
      setFormData(initialParameters);
    }
  }, [open, initialParameters]);

  const parameters = workflow?.parameters ?? [];

  const isValid = useMemo(() => {
    return parameters
      .filter(p => p.required)
      .every(p => {
        const val = formData[p.name];
        if (val === undefined || val === null || val === '') return false;
        return true;
      });
  }, [parameters, formData]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setFormData({});
      setIsSubmitting(false);
    }
    onOpenChange(nextOpen);
  }

  function updateField(name: string, value: unknown) {
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit() {
    if (!workflow || !isValid) return;
    setIsSubmitting(true);
    try {
      await api.post(`/workflows/${workflow.id}/execute`, { parameters: formData });
      toast.success(t('integration.flow.execute.success', { name: workflow.name }));
      handleOpenChange(false);
      onSuccess();
    } catch {
      toast.error(t('integration.flow.execute.error', { name: workflow.name }));
    } finally {
      setIsSubmitting(false);
    }
  }

  function renderField(param: WorkflowParameter) {
    const fieldId = `flow-param-${param.name}`;
    switch (param.type) {
      case 'text':
        return (
          <Input
            id={fieldId}
            data-testid={fieldId}
            value={(formData[param.name] as string) ?? param.defaultValue ?? ''}
            onChange={e => updateField(param.name, e.target.value)}
            aria-required={param.required}
          />
        );
      case 'number':
        return (
          <Input
            id={fieldId}
            data-testid={fieldId}
            type="number"
            value={(formData[param.name] as string) ?? param.defaultValue ?? ''}
            onChange={e => updateField(param.name, e.target.value)}
            aria-required={param.required}
          />
        );
      case 'date':
        return (
          <Input
            id={fieldId}
            data-testid={fieldId}
            type="date"
            value={(formData[param.name] as string) ?? ''}
            onChange={e => updateField(param.name, e.target.value)}
            aria-required={param.required}
          />
        );
      case 'select':
        return (
          <Select
            value={(formData[param.name] as string) ?? (param.defaultValue as string) ?? ''}
            onValueChange={val => updateField(param.name, val)}
          >
            <SelectTrigger id={fieldId} data-testid={fieldId} aria-required={param.required}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {param.options?.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'toggle':
        return (
          <Switch
            id={fieldId}
            data-testid={fieldId}
            checked={(formData[param.name] as boolean) ?? (param.defaultValue as boolean) ?? false}
            onCheckedChange={val => updateField(param.name, val)}
          />
        );
      default:
        return null;
    }
  }

  if (!workflow) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="flow-execute-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('integration.flow.execute.title')}: {workflow.name}
          </DialogTitle>
        </DialogHeader>

        {parameters.length > 0 && (
          <div className="space-y-4 py-2">
            {parameters.map(param => (
              <div key={param.name} className="space-y-1.5">
                <Label htmlFor={`flow-param-${param.name}`}>
                  {param.label}
                  {param.required && <span className="text-destructive ml-1">*</span>}
                </Label>
                {renderField(param)}
              </div>
            ))}
          </div>
        )}

        {workflow.steps.length > 0 && (
          <div className="border-t pt-3 mt-2">
            <p className="text-xs font-medium text-muted-foreground mb-1">
              {t('integration.flow.execute.steps')}
            </p>
            <ol className="text-xs text-muted-foreground list-decimal list-inside space-y-0.5">
              {workflow.steps.map(step => (
                <li key={step.id}>{step.name}</li>
              ))}
            </ol>
          </div>
        )}

        <DialogFooter>
          <Button
            onClick={() => void handleSubmit()}
            disabled={!isValid || isSubmitting}
            data-testid="flow-execute-submit-btn"
            aria-busy={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('integration.flow.execute.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
